import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Flask, X, Plus, MessengerLogo, InstagramLogo } from "@phosphor-icons/react";

const ChannelIcons = ({ channels }) => (
  <span className="inline-flex gap-0.5">
    {channels.includes("messenger") && <MessengerLogo size={13} weight="fill" className="text-blue-500" />}
    {channels.includes("instagram") && <InstagramLogo size={13} weight="fill" className="text-pink-500" />}
  </span>
);

export default function BotTestModeCard() {
  const [cfg, setCfg] = useState(null);
  const [q, setQ] = useState("");
  const [cands, setCands] = useState([]);

  useEffect(() => { api.get("/settings/bot-test-mode").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const t = setTimeout(() => api.get("/settings/bot-test-mode/candidates", { params: { q } })
      .then((r) => setCands(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);

  const run = (p, ok) => p.then((r) => { setCfg(r.data); if (ok) toast.success(ok); })
    .catch((e) => toast.error(e?.response?.data?.detail || "Error"));
  if (!cfg) return null;
  const ids = new Set(cfg.testers.map((t) => t.id));
  const options = cands.filter((c) => !ids.has(c.id));

  return (
    <div className={`rounded-lg border p-4 ${cfg.enabled ? "bg-amber-500/5 border-amber-500/40" : "bg-card border-border"}`} data-testid="bot-test-mode-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full flex items-center justify-center bg-amber-500/15 text-amber-600 shrink-0"><Flask size={20} weight="fill" /></div>
          <div className="min-w-0">
            <div className="font-heading font-bold text-sm" data-testid="bot-test-mode-status">
              Modo prueba · Messenger e Instagram · {cfg.enabled ? "ACTIVO" : "Apagado"}
            </div>
            <div className="text-[11px] text-muted-foreground">
              {cfg.enabled
                ? "El bot solo responde a las personas de la lista. Los demás llegan a la Bandeja sin respuesta del bot ni envíos automáticos. WhatsApp no se afecta."
                : "El bot responde a todos en Messenger e Instagram."}
            </div>
          </div>
        </div>
        <Switch checked={cfg.enabled} onCheckedChange={(v) => run(api.patch("/settings/bot-test-mode", { enabled: v }),
          v ? "Modo prueba activado" : "Modo prueba apagado: el bot responde a todos")} data-testid="bot-test-mode-switch" />
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5" data-testid="bot-test-mode-testers">
        {(cfg.ig_usernames || []).filter((u) => !cfg.testers.some((t) => (t.name || "").toLowerCase() === `@${u}`)).map((u) => (
          <span key={u} className="inline-flex items-center gap-1 rounded-full border border-dashed border-pink-400/60 bg-background pl-2 pr-1 py-0.5 text-xs" data-testid={`bot-tester-ig-${u}`}
            title="Se activa cuando esta cuenta escriba por primera vez">
            <InstagramLogo size={13} weight="fill" className="text-pink-500" /> @{u} <span className="text-[10px] text-muted-foreground">· esperando su primer mensaje</span>
            <button onClick={() => run(api.delete(`/settings/bot-test-mode/ig-usernames/${u}`))} className="p-0.5 rounded-full hover:bg-muted" data-testid={`bot-tester-ig-remove-${u}`}><X size={11} /></button>
          </span>
        ))}
        {cfg.testers.length === 0 && !(cfg.ig_usernames || []).length && <span className="text-[11px] text-muted-foreground italic">Sin personas de prueba: el bot no responde a nadie en estos canales.</span>}
        {cfg.testers.map((t) => (
          <span key={t.id} className="inline-flex items-center gap-1 rounded-full border border-border bg-background pl-2 pr-1 py-0.5 text-xs" data-testid={`bot-tester-${t.id}`}>
            <ChannelIcons channels={t.channels} /> {t.name}
            <button onClick={() => run(api.delete(`/settings/bot-test-mode/testers/${t.id}`))} className="p-0.5 rounded-full hover:bg-muted" data-testid={`bot-tester-remove-${t.id}`}><X size={11} /></button>
          </span>
        ))}
      </div>

      <div className="mt-3">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar contacto de Messenger o Instagram, o escribir un @usuario de Instagram…"
          className="h-8 text-xs" data-testid="bot-test-mode-search" />
        {q.trim().length >= 2 && /^@?[A-Za-z0-9._]{1,30}$/.test(q.trim()) && !options.length && (
          <Button size="sm" variant="outline" className="mt-1.5 h-7 text-xs gap-1.5" data-testid="bot-test-mode-add-ig"
            onClick={() => run(api.post("/settings/bot-test-mode/ig-usernames", { username: q.trim() }), `@${q.trim().replace(/^@/, "")} agregado: el bot le responderá cuando escriba`).then(() => setQ(""))}>
            <InstagramLogo size={13} weight="fill" className="text-pink-500" /> Agregar @{q.trim().replace(/^@/, "")} como persona de prueba de Instagram
          </Button>
        )}
        {options.length > 0 && (
          <div className="mt-1.5 max-h-40 overflow-y-auto rounded-md border border-border divide-y divide-border" data-testid="bot-test-mode-candidates">
            {options.map((c) => (
              <div key={c.id} className="flex items-center justify-between px-2 py-1.5 text-xs">
                <span className="flex items-center gap-1.5"><ChannelIcons channels={c.channels} /> {c.name}</span>
                <Button size="sm" variant="ghost" className="h-6 px-2 text-xs gap-1" onClick={() => run(api.post(`/settings/bot-test-mode/testers/${c.id}`), `${c.name} agregado a la prueba`)}
                  data-testid={`bot-tester-add-${c.id}`}><Plus size={11} /> Agregar</Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
